import { h, Component } from 'preact';
import { calcChange, formatNumber } from '../../lib/util';
import { getStockPriceHistory } from '@os/cloudquote';
import { Text } from 'preact-i18n';
import { FinanceSpinner } from '../glyphs';
import { HorizontalBarGraph, VerticalBarGraph } from '../bar-graph';
import { ChangeBoxes } from '../change-box';
import style from './style';

const MONTH_NS = 'stock-performance.months';
const DAYS_NS = 'stock-performance.daysOfWeek';

let daysOfWeek = [
	`${DAYS_NS}.sunday`,
	`${DAYS_NS}.monday`,
	`${DAYS_NS}.tuesday`,
	`${DAYS_NS}.wednesday`,
	`${DAYS_NS}.thursday`,
	`${DAYS_NS}.friday`,
	`${DAYS_NS}.saturday`
];

let months = [
	`${MONTH_NS}.jan`,
	`${MONTH_NS}.feb`,
	`${MONTH_NS}.mar`,
	`${MONTH_NS}.apr`,
	`${MONTH_NS}.may`,
	`${MONTH_NS}.jun`,
	`${MONTH_NS}.jul`,
	`${MONTH_NS}.aug`,
	`${MONTH_NS}.sep`,
	`${MONTH_NS}.oct`,
	`${MONTH_NS}.nov`,
	`${MONTH_NS}.dec`
];


export const StockPerformanceByInterval = ({ quote }) => {
	if (!quote) return (
		<div class={style['stock-performance-interval']}>
			<FinanceSpinner />
		</div>
	);

	let bars = [
		{
			barLabel: <Text id="stock-performance.ytd" />,
			change: calcChange(quote.Price, quote.StartingPriceYTD)
		},
		{
			barLabel: <Text id="stock-performance.oneMonth" />,
			change: calcChange(quote.Price, quote.StartingPrice1M)
		},
		{
			barLabel: <Text id="stock-performance.threeMonth" />,
			change: calcChange(quote.Price, quote.StartingPrice3M)
		},
		{
			barLabel: <Text id="stock-performance.sixMonth" />,
			change: calcChange(quote.Price, quote.StartingPrice6M)
		},
		{
			barLabel: <Text id="stock-performance.oneYear" />,
			change: calcChange(quote.Price, quote.StartingPrice52)
		}
	];
	let max = 5;
	for (let i=0; i<bars.length; i++) {
		let bar = bars[i];
		let abs = Math.abs(bar.change.percent);
		if (abs > max) max = abs;
	}
	max *= 1.1;
	for (let i=0; i<bars.length; i++) {
		let bar = bars[i];
		bar.valLabel = formatNumber(bar.change.percent, { Decimals: 2, Default: '', Plus: true, Suffix: '%' });
		bar.value = (max + bar.change.percent) / (max * 2);
		bar.className = bar.change.class;
	}

	return (
		<div class={style['stock-performance-interval']}>
			<div class={style['horiz-graph']}>
				<HorizontalBarGraph {...{ bars, width: 365, height: 200, labelPos: 'left', barStartValue: 0.5 }} />
			</div>
		</div>
	);
};

export class StockPerformanceByMonth extends Component {
	calcBars(data) {
		if (data) {
			let closingRecords = {};
			if (data.rows) {
				for (let i=0; i<data.rows.length; i++) {
					let row = data.rows[i];
					let d = new Date(row[0]);
					let ym = (d.getYear() * 12) + (d.getMonth() - 1);
					closingRecords[ym] = row;
				}
			}
			let min = -25, max = 25; //At least 25% range
			let bars = [];
			let yms = Object.keys(closingRecords).sort((a,b) => b - a);
			for (let i = 1; i < 7 && i < yms.length; i++) {
				let newer = closingRecords[yms[i-1]];
				let older = closingRecords[yms[i]];
				let change = calcChange(newer[4],older[4]);
				min = change.percent > min ? min : change.percent;
				max = change.percent < max ? max : change.percent;

				let d = new Date(newer[0]);
				bars[i-1] = {
					change,
					className: change.class,
					valLabel: formatNumber(change.percent, { Decimals: 2, Default: '', Plus: true, Suffix: '%' }),
					barLabel: <Text id={months[d.getMonth()]} />
				};
			}
			for (let i=0; i<bars.length; i++) {
				let bar = bars[i];
				bar.value = (bar.change.percent - min) / (max - min);
			}

			this.setState({ bars });
		}
	}

	componentWillMount() {
		getStockPriceHistory(this.props.symbol)
			.then((data) => {
				this.calcBars(data);
			});
	}

	render(props, { bars }) {
		if (!bars || !bars.length) return (
			<div class={style['stock-performance-month']}>
				<FinanceSpinner />
			</div>
		);

		return (
			<div class={style['stock-performance-month']}>
				<HorizontalBarGraph {...{ bars, width: 300, height: 200, labelPos: 'left' }} />
				<VerticalBarGraph {...{ bars, width: 450, height: 200 }} />
			</div>
		);
	}
}

export class StockPerformanceByDay extends Component {
	calcLast5(data) {
		if (!data) return;

		let last5 = [];

		if (data.rows) {
			let rows = data.rows.slice().reverse();
			for (let i = 1; i < 6 && i < rows.length; i++) {
				last5[i-1] = {
					date: rows[i-1][0],
					change: calcChange(rows[i-1][4],rows[i][4])
				};
			}
		}

		this.setState({ last5 });
	}

	calculateChangeBoxes (last5) {
		return last5.map( (row) => {
			let date = new Date(row.date);
			let dayStr = daysOfWeek[date.getUTCDay()];
			let dateStr = months[date.getUTCMonth()] + ' ' + date.getUTCDate();
			return {
				className: row.change.class,
				label: <Text id={dayStr} />,
				sub: dateStr,
				value: formatNumber(row.change.percent,{ Decimals: 2, Default: '0.00%', Plus: true, Suffix: '%' })
			};
		});
	}
	componentWillMount() {
		getStockPriceHistory(this.props.symbol)
			.then((data) => {
				this.calcLast5(data);
			});
	}

	render(props, { last5 }) {
		if (!last5) {
			return <div class={style['stock-performance-day']}><FinanceSpinner /></div>;
		}
		let boxes = this.calculateChangeBoxes(last5);
		return (
			<div class={style['stock-performance-day']}>
				<div class={style['section-label']}><Text id="stock-performance.last5Days">Last 5 Days</Text></div>
				<ChangeBoxes boxes={boxes} />
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ../node_modules/@cube/finance-components/src/components/stock-performance/index.js